'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSaveMyNavSettings } from '../../lib/mutations';
import { useMyNavSettings } from '../../lib/queries';
import { authGeneration } from '../../lib/token';
import type { NavLayout } from '../../lib/types';
import { useTranslation } from '../../lib/i18n';
import { useToast } from '../ui/Toast';

/** ResizeHandle owns gestures; this owner writes geometry without rendering the menu on pointer moves.
 * The wire range is navSettings.ts's 240–480 CSS pixels; the skin default is the UI floor and the
 * measured workspace caps the column at 35%. A smaller device never rewrites the account preference. */
export function useSidebarResize({ layout, userId, expanded, regionWidth, scale, side }: {
  layout: NavLayout;
  userId: number | null;
  expanded: boolean;
  regionWidth: number;
  scale: number;
  side: 'left' | 'right';
}) {
  const navRef = useRef<HTMLElement>(null);
  const qc = useQueryClient();
  const settings = useMyNavSettings();
  const save = useSaveMyNavSettings();
  const { t } = useTranslation();
  const { toast } = useToast();
  // Only a real server snapshot enables editing: a pre-update daemon does not know this field.
  const supported = settings.data?.sidebarWidth !== undefined;
  const enabled = expanded && supported && userId !== null && regionWidth > 0;
  const current = useRef(0);
  const bounds = useRef({ min: 0, max: 0 });
  const draft = useRef(false);
  const changed = useRef(false);
  const edit = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [range, setRange] = useState({ value: 0, min: 0, max: 0 });

  const paint = (width: number) => {
    const { min, max } = bounds.current;
    current.current = Math.max(min, Math.min(max, width));
    const pixels = Math.round(current.current);
    const nav = navRef.current!;
    nav.style.setProperty('--sidebar-user-width', `${pixels}px`);
    nav.querySelector('[role="separator"]')?.setAttribute('aria-valuenow', String(pixels));
  };

  useLayoutEffect(() => {
    draft.current = false;
    changed.current = false;
    edit.current++;
    return () => { clearTimeout(timer.current); };
  }, [userId]);

  useLayoutEffect(() => {
    const nav = navRef.current!;
    if (!expanded || regionWidth === 0 || layout.sidebarWidth === undefined) {
      nav.style.removeProperty('--sidebar-user-width');
      delete nav.dataset.sized;
      return;
    }
    const update = () => {
      // Resolve the skin token through the browser, not a private rem/px parser. This read happens only
      // when preferences, applied skin or available room change, never in the pointer-move path.
      nav.dataset.sized = 'true';
      nav.style.width = 'var(--sidebar-width)';
      const defaultWidth = Number.parseFloat(getComputedStyle(nav).width);
      nav.style.removeProperty('width');
      if (!Number.isFinite(defaultWidth)) throw new Error('Sidebar width token must resolve to a CSS length');
      const min = Math.max(240, Math.min(480, Math.round(defaultWidth)));
      const max = Math.max(min, Math.min(480, Math.floor(regionWidth * 0.35)));
      bounds.current = { min, max };
      paint(draft.current ? current.current : layout.sidebarWidth ?? min);
      setRange({ value: Math.round(current.current), min, max });
    };
    update();
    // Like EffectsProvider, observe the applied design: a descendant's layout effect runs before the
    // SkinProvider applies its new attributes, while this callback sees the tokens actually in force.
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-skin'] });
    return () => observer.disconnect();
  }, [expanded, regionWidth, layout.sidebarWidth, userId]);

  const finish = (reset = false) => {
    if (!enabled || (!reset && !changed.current)) return;
    changed.current = false;
    const sidebarWidth = reset ? null : Math.round(current.current);
    const version = ++edit.current;
    const generation = authGeneration();
    clearTimeout(timer.current);
    setRange({ ...bounds.current, value: Math.round(current.current) });
    timer.current = setTimeout(() => {
      if (generation !== authGeneration()) return;
      qc.setQueryData<NavLayout>(['my-nav-settings'], previous => ({ ...previous!, sidebarWidth }));
      save.mutate({ sidebarWidth }, {
        onError: error => {
          if (generation === authGeneration()) toast(t.common.saveFailed, 'error', { error });
        },
        onSettled: () => {
          if (generation !== authGeneration() || version !== edit.current) return;
          draft.current = false;
          const stored = qc.getQueryData<NavLayout>(['my-nav-settings'])?.sidebarWidth;
          if (navRef.current && expanded) {
            paint(stored ?? bounds.current.min);
            setRange({ ...bounds.current, value: Math.round(current.current) });
          }
        },
      });
    }, 300);
  };

  return {
    navRef, enabled, range,
    onDelta: (delta: number) => {
      if (!enabled) return;
      const next = Math.max(bounds.current.min, Math.min(bounds.current.max,
        current.current + (side === 'left' ? delta : -delta) / scale));
      if (next === current.current) return;
      // A second gesture before the debounce expires supersedes the previous release.
      clearTimeout(timer.current);
      edit.current++;
      draft.current = true;
      changed.current = true;
      paint(next);
    },
    onEnd: () => finish(),
    onReset: () => {
      draft.current = true;
      paint(bounds.current.min);
      finish(true);
    },
  };
}
